import { useEffect, useRef, useCallback } from 'react';
import {
	ScreenSpaceEventHandler,
	ScreenSpaceEventType,
	ClippingPlaneCollection,
	ClippingPlane,
	Cartesian3,
	Color,
	CallbackProperty,
	defined,
	Matrix4,
	Cesium3DTileset,
	Viewer,
	Entity,
	Plane,
	ColorMaterialProperty,
	IntersectionTests,
	Matrix3,
	Quaternion,
	Math as CesiumMath,
} from 'cesium';
import { toast } from 'sonner';
import { boxFrame, type ClipBox } from '@/helper/clipping-box-geometry';
import { clipWithBox, pauseBoxClipping } from '@/helper/clipping-box-planes';
import { t } from '@/i18n';

export type UseClippingBoxOptions = {
	// The box to show. A new object (the panel opened or placed it) replaces the
	// current box; drags change the box in place and report it via onSaveState.
	box: ClipBox | null;
	enableScale: boolean;
	enableRotate: boolean;
	enableMove: boolean;
	onSaveState: (box: ClipBox) => void;
};

const FACES_CONFIG = [
	{ id: 'xMax', normal: new Cartesian3(-1, 0, 0), axis: 'x', sign: 1, opp: 'xMin' },
	{ id: 'xMin', normal: new Cartesian3(1, 0, 0), axis: 'x', sign: -1, opp: 'xMax' },
	{ id: 'yMax', normal: new Cartesian3(0, -1, 0), axis: 'y', sign: 1, opp: 'yMin' },
	{ id: 'yMin', normal: new Cartesian3(0, 1, 0), axis: 'y', sign: -1, opp: 'yMax' },
	{ id: 'zMax', normal: new Cartesian3(0, 0, -1), axis: 'z', sign: 1, opp: 'zMin' },
	{ id: 'zMin', normal: new Cartesian3(0, 0, 1), axis: 'z', sign: -1, opp: 'zMax' },
];

type DragState = {
	type: 'resize' | 'translate' | 'rotate' | null;
	id: string | null;
	startWorldPos: Cartesian3 | null;
	startBounds: any;
	startRotation: { heading: number; pitch: number; roll: number };
	startTransform?: Matrix4;
};

const createRingPositions = (axis: 'x' | 'y' | 'z', radius: number, segments: number = 64) => {
	const positions: Cartesian3[] = [];
	for (let i = 0; i <= segments; i++) {
		const angle = (i / segments) * Math.PI * 2;
		const cos = Math.cos(angle) * radius;
		const sin = Math.sin(angle) * radius;
		// Fix: Map rings exactly to their specific planes to rotate around their specific axes
		if (axis === 'x') positions.push(new Cartesian3(0, cos, sin)); // Rotate around X axis (YZ plane)
		if (axis === 'y') positions.push(new Cartesian3(cos, 0, sin)); // Rotate around Y axis (XZ plane)
		if (axis === 'z') positions.push(new Cartesian3(cos, sin, 0)); // Rotate around Z axis (XY plane)
	}
	return positions;
};

export const useClippingBox = (
	viewer: Viewer | null,
	clipTilesets: Cesium3DTileset[],
	enabled: boolean,
	options: UseClippingBoxOptions
) => {
	const handlerRef = useRef<ScreenSpaceEventHandler | null>(null);

	// Entity Refs for Cleanup & Visibility Toggling
	const boxEntitiesRef = useRef<Entity[]>([]);
	const centerEntityRef = useRef<Entity | null>(null);
	const resizeEntitiesRef = useRef<Entity[]>([]);
	const rotateEntitiesRef = useRef<Entity[]>([]);

	const clippingCollectionsRef = useRef<
		{ collection: ClippingPlaneCollection; tileset: Cesium3DTileset }[]
	>([]);
	const allPlanesRef = useRef<Record<string, ClippingPlane>[]>([]);

	const centerRef = useRef<ClipBox['center'] | null>(null);
	const currentTransformRef = useRef<Matrix4>(new Matrix4());

	const bounds = useRef({ xMax: 50, xMin: -50, yMax: 50, yMin: -50, zMax: 50, zMin: -50 });
	const rotationRef = useRef({ heading: 0, pitch: 0, roll: 0 });

	const hoveredElementRef = useRef<string | null>(null);
	const dragStateRef = useRef<DragState>({
		type: null,
		id: null,
		startWorldPos: null,
		startBounds: null,
		startRotation: { heading: 0, pitch: 0, roll: 0 },
	});

	const optionsRef = useRef(options);

	// Keep optionsRef synced
	useEffect(() => {
		optionsRef.current = options;
	}, [options]);

	// Imperatively force entities to hide/show when toggles change
	useEffect(() => {
		if (!viewer) return;

		if (centerEntityRef.current) {
			centerEntityRef.current.show = options.enableMove as any;
		}
		resizeEntitiesRef.current.forEach((entity) => {
			entity.show = options.enableScale as any;
		});
		rotateEntitiesRef.current.forEach((entity) => {
			entity.show = options.enableRotate as any;
		});

		viewer.scene.requestRender();
	}, [options.enableMove, options.enableScale, options.enableRotate, viewer]);

	const updatePlanes = useCallback(() => {
		const b = bounds.current;
		allPlanesRef.current.forEach((planes) => {
			planes.xMax.distance = b.xMax;
			planes.xMin.distance = -b.xMin;
			planes.yMax.distance = b.yMax;
			planes.yMin.distance = -b.yMin;
			planes.zMax.distance = b.zMax;
			planes.zMin.distance = -b.zMin;
		});
	}, []);

	const updateTransforms = useCallback(() => {
		if (!centerRef.current || !viewer) return;

		boxFrame({ center: centerRef.current, rotation: rotationRef.current }, currentTransformRef.current);

		clippingCollectionsRef.current.forEach(({ collection, tileset }) => {
			// Cesium reads a tileset's clipping planes in its clipping origin: the root
			// transform, plus an ENU frame for tilesets with ECEF coordinates baked in.
			// Not in the typings, but it is the matrix Cesium itself uses.
			const origin = (tileset as unknown as { clippingPlanesOriginMatrix: Matrix4 })
				.clippingPlanesOriginMatrix;
			collection.modelMatrix = Matrix4.multiply(
				Matrix4.inverse(origin, new Matrix4()),
				currentTransformRef.current,
				new Matrix4()
			);
		});

		viewer.scene.requestRender();
	}, [viewer]);

	// Take over a box the panel opened or placed.
	const { box } = options;
	useEffect(() => {
		if (!box) return;
		centerRef.current = box.center;
		rotationRef.current = { ...box.rotation };
		bounds.current = { ...box.bounds };
		updateTransforms();
		updatePlanes();
	}, [box, updateTransforms, updatePlanes]);

	const hasBox = box !== null;

	// Clip the given layers. Kept apart from the handles, so a layer that loads only
	// swaps clipping collections and never rebuilds the box.
	useEffect(() => {
		if (!viewer || !enabled || !hasBox || clipTilesets.length === 0) return;

		try {
			clipTilesets.forEach((tileset) => {
				const collection = clipWithBox(tileset, () =>
					FACES_CONFIG.map((config) => new ClippingPlane(config.normal, 10000))
				);
				// The planes are in FACES_CONFIG order, also in a collection kept from an earlier box.
				const planesObj: Record<string, ClippingPlane> = {};
				FACES_CONFIG.forEach((config, index) => {
					planesObj[config.id] = collection.get(index);
				});

				allPlanesRef.current.push(planesObj);
				clippingCollectionsRef.current.push({ collection, tileset });
			});

			updateTransforms();
			updatePlanes();
		} catch (err) {
			console.error(err);
			toast.error(t('viewer.clipping.toast.initFailed'));
		}

		return () => {
			// Switched off, never destroyed here: see clipping-box-planes.ts.
			clippingCollectionsRef.current.forEach(({ tileset }) => {
				if (!tileset.isDestroyed()) pauseBoxClipping(tileset);
			});
			clippingCollectionsRef.current = [];
			allPlanesRef.current = [];

			if (!viewer.isDestroyed()) viewer.scene.requestRender();
		};
	}, [viewer, enabled, hasBox, clipTilesets, updateTransforms, updatePlanes]);

	// Show the box and wire up its handles.
	useEffect(() => {
		if (!viewer || !enabled || !hasBox) return;

		const scene = viewer.scene;

		try {
			const createHoverProperty = (targetId: string, baseColor: Color, hoverColor: Color) => {
				return new CallbackProperty(() => {
					return hoveredElementRef.current === targetId || dragStateRef.current.id === targetId
						? hoverColor
						: baseColor;
				}, false);
			};

			// Central Center Point (Translate)
			const centerEntity = viewer.entities.add({
				name: 'translate_center',
				show: optionsRef.current.enableMove,
				position: new CallbackProperty(() => {
					const b = bounds.current;
					const local = new Cartesian3(
						(b.xMax + b.xMin) / 2,
						(b.yMax + b.yMin) / 2,
						(b.zMax + b.zMin) / 2
					);
					return Matrix4.multiplyByPoint(currentTransformRef.current, local, new Cartesian3());
				}, false),
				point: {
					pixelSize: 22,
					color: createHoverProperty('translate_center', Color.WHITE, Color.YELLOW),
					outlineColor: Color.BLACK,
					outlineWidth: 2,
					disableDepthTestDistance: Number.POSITIVE_INFINITY,
				},
			});
			centerEntityRef.current = centerEntity;
			boxEntitiesRef.current.push(centerEntity);

			// Setup Faces and Resize Handles
			FACES_CONFIG.forEach((config) => {
				const positionCallback = new CallbackProperty(() => {
					const b = bounds.current as any;
					const local = new Cartesian3(
						config.axis === 'x' ? b[config.id] : (b.xMax + b.xMin) / 2,
						config.axis === 'y' ? b[config.id] : (b.yMax + b.yMin) / 2,
						config.axis === 'z' ? b[config.id] : (b.zMax + b.zMin) / 2
					);
					return Matrix4.multiplyByPoint(currentTransformRef.current, local, new Cartesian3());
				}, false);

				const orientationCallback = new CallbackProperty(() => {
					return Quaternion.fromRotationMatrix(
						Matrix4.getMatrix3(currentTransformRef.current, new Matrix3())
					);
				}, false);

				// Translucent Walls
				boxEntitiesRef.current.push(
					viewer.entities.add({
						name: 'wall_' + config.id,
						position: positionCallback,
						orientation: orientationCallback,
						box: {
							dimensions: new CallbackProperty(() => {
								const b = bounds.current as any;
								const thickness =
									Math.max(b.xMax - b.xMin, b.yMax - b.yMin, b.zMax - b.zMin) * 0.005;
								const sizeX = Math.max(0.1, b.xMax - b.xMin);
								const sizeY = Math.max(0.1, b.yMax - b.yMin);
								const sizeZ = Math.max(0.1, b.zMax - b.zMin);

								if (config.axis === 'x') return new Cartesian3(thickness, sizeY, sizeZ);
								if (config.axis === 'y') return new Cartesian3(sizeX, thickness, sizeZ);
								return new Cartesian3(sizeX, sizeY, thickness);
							}, false),
							material: new ColorMaterialProperty(
								new CallbackProperty(() => {
									return dragStateRef.current.id === 'resize_' + config.id
										? Color.CYAN.withAlpha(0.3)
										: Color.WHITE.withAlpha(0.05);
								}, false)
							),
							outline: true,
							outlineColor: Color.WHITE.withAlpha(0.3),
						},
					})
				);

				// Resize Handles
				if (config.id !== 'zMin') {
					const resizeEntity = viewer.entities.add({
						name: 'resize_' + config.id,
						show: optionsRef.current.enableScale,
						position: positionCallback,
						point: {
							pixelSize: new CallbackProperty(
								() => (dragStateRef.current.id === 'resize_' + config.id ? 20 : 15),
								false
							),
							color: createHoverProperty('resize_' + config.id, Color.CYAN, Color.YELLOW),
							outlineColor: Color.BLACK,
							outlineWidth: 2,
							disableDepthTestDistance: Number.POSITIVE_INFINITY,
						},
					});
					resizeEntitiesRef.current.push(resizeEntity);
					boxEntitiesRef.current.push(resizeEntity);
				}
			});

			// 3D Rotation Arcs and Gizmo
			const rotConfigs = [
				{ id: 'x', color: Color.RED },
				{ id: 'y', color: Color.GREEN },
				{ id: 'z', color: Color.BLUE },
			];

			rotConfigs.forEach((rc) => {
				const rotateEntity = viewer.entities.add({
					name: 'rotate_' + rc.id,
					show: optionsRef.current.enableRotate,
					polyline: {
						positions: new CallbackProperty(() => {
							const b = bounds.current;
							const extX = b.xMax - b.xMin;
							const extY = b.yMax - b.yMin;
							const extZ = b.zMax - b.zMin;
							const radius = Math.max(extX, extY, extZ) * 0.6;
							const centerLocal = new Cartesian3(
								(b.xMax + b.xMin) / 2,
								(b.yMax + b.yMin) / 2,
								(b.zMax + b.zMin) / 2
							);
							const localRing = createRingPositions(rc.id as 'x' | 'y' | 'z', radius);

							return localRing.map((p) => {
								const offsetPos = Cartesian3.add(centerLocal, p, new Cartesian3());
								return Matrix4.multiplyByPoint(
									currentTransformRef.current,
									offsetPos,
									new Cartesian3()
								);
							});
						}, false),
						width: new CallbackProperty(() => {
							return hoveredElementRef.current === 'rotate_' + rc.id ||
								dragStateRef.current.id === 'rotate_' + rc.id
								? 10
								: 5;
						}, false),
						material: new ColorMaterialProperty(
							createHoverProperty('rotate_' + rc.id, rc.color, Color.YELLOW)
						),
						depthFailMaterial: new ColorMaterialProperty(
							createHoverProperty(
								'rotate_' + rc.id,
								rc.color.withAlpha(0.2),
								Color.YELLOW.withAlpha(0.2)
							)
						),
					},
				});
				rotateEntitiesRef.current.push(rotateEntity);
				boxEntitiesRef.current.push(rotateEntity);
			});

			// Interaction Logic
			const handler = new ScreenSpaceEventHandler(scene.canvas);
			handlerRef.current = handler;

			// Hover Effects
			const hover = (movement: any) => {
				const picked = scene.pick(movement.endPosition);

				if (defined(picked) && defined(picked.id) && typeof picked.id.name === 'string') {
					const name = picked.id.name;
					// Ignore interaction completely if disabled
					if (name.startsWith('resize_') && !optionsRef.current.enableScale) return;
					if (name.startsWith('translate_') && !optionsRef.current.enableMove) return;
					if (name.startsWith('rotate_') && !optionsRef.current.enableRotate) return;

					if (
						name.startsWith('resize_') ||
						name.startsWith('translate_') ||
						name.startsWith('rotate_')
					) {
						hoveredElementRef.current = name;
						viewer.container.style.cursor = 'grab';
						scene.requestRender();
						return;
					}
				}
				if (hoveredElementRef.current) {
					hoveredElementRef.current = null;
					viewer.container.style.cursor = 'default';
					scene.requestRender();
				}
			};

			// Drag Start
			handler.setInputAction((movement: any) => {
				const picked = scene.pick(movement.position);
				if (defined(picked) && defined(picked.id) && typeof picked.id.name === 'string') {
					const name = picked.id.name;

					if (name.startsWith('resize_') && !optionsRef.current.enableScale) return;
					if (name.startsWith('translate_') && !optionsRef.current.enableMove) return;
					if (name.startsWith('rotate_') && !optionsRef.current.enableRotate) return;

					const camera = scene.camera;
					const ray = camera.getPickRay(movement.position);
					if (!ray) return;

					scene.screenSpaceCameraController.enableInputs = false;
					viewer.container.style.cursor = 'grabbing';

					dragStateRef.current = {
						type: name.startsWith('resize_')
							? 'resize'
							: name.startsWith('translate_')
								? 'translate'
								: 'rotate',
						id: name,
						startWorldPos: null,
						startBounds: JSON.parse(JSON.stringify(bounds.current)),
						startRotation: { ...rotationRef.current },
						startTransform: currentTransformRef.current.clone(),
					};

					if (dragStateRef.current.type === 'translate' || dragStateRef.current.type === 'resize') {
						const origin = picked.id.position
							? picked.id.position.getValue(scene.time)
							: Matrix4.getTranslation(currentTransformRef.current, new Cartesian3());
						const normal = Cartesian3.negate(camera.direction, new Cartesian3());
						const dragPlane = Plane.fromPointNormal(origin, normal);
						dragStateRef.current.startWorldPos = IntersectionTests.rayPlane(ray, dragPlane);
					} else if (dragStateRef.current.type === 'rotate') {
						const axisId = name.replace('rotate_', '');
						const worldAxisNormal = new Cartesian3();

						// Assign exact column index based on X(0), Y(1), Z(2) mapping
						let colIndex = 2; // Z axis default
						if (axisId === 'x') colIndex = 0; // X axis
						if (axisId === 'y') colIndex = 1; // Y axis

						Matrix4.getColumn(currentTransformRef.current, colIndex, worldAxisNormal);

						const centerLocal = new Cartesian3(
							(bounds.current.xMax + bounds.current.xMin) / 2,
							(bounds.current.yMax + bounds.current.yMin) / 2,
							(bounds.current.zMax + bounds.current.zMin) / 2
						);
						const worldCenter = Matrix4.multiplyByPoint(
							currentTransformRef.current,
							centerLocal,
							new Cartesian3()
						);

						const dragPlane = Plane.fromPointNormal(worldCenter, worldAxisNormal);
						const hit = IntersectionTests.rayPlane(ray, dragPlane);

						if (hit) dragStateRef.current.startWorldPos = hit;
					}
				}
			}, ScreenSpaceEventType.LEFT_DOWN);

			// Drag Move
			const drag = (movement: any) => {
				const state = dragStateRef.current;
				if (!state.type || !state.id || !state.startWorldPos || !state.startTransform) return;

				const camera = scene.camera;
				const ray = camera.getPickRay(movement.endPosition);
				if (!ray) return;

				// Translation Logic
				if (state.type === 'translate') {
					const normal = Cartesian3.negate(camera.direction, new Cartesian3());
					const dragPlane = Plane.fromPointNormal(state.startWorldPos, normal);
					const hit = IntersectionTests.rayPlane(ray, dragPlane);

					if (hit) {
						const worldDelta = Cartesian3.subtract(hit, state.startWorldPos, new Cartesian3());
						const inverse = Matrix4.inverse(state.startTransform, new Matrix4());
						const localDelta = Matrix4.multiplyByPointAsVector(
							inverse,
							worldDelta,
							new Cartesian3()
						);

						bounds.current.xMax = state.startBounds.xMax + localDelta.x;
						bounds.current.xMin = state.startBounds.xMin + localDelta.x;
						bounds.current.yMax = state.startBounds.yMax + localDelta.y;
						bounds.current.yMin = state.startBounds.yMin + localDelta.y;
						bounds.current.zMax = state.startBounds.zMax + localDelta.z;
						bounds.current.zMin = state.startBounds.zMin + localDelta.z;

						updatePlanes();
						scene.requestRender();
					}
				}

				// Rotation Logic
				else if (state.type === 'rotate') {
					const axisId = state.id.replace('rotate_', '');
					const worldAxisNormal = new Cartesian3();

					// Same standard indices mapping as `LEFT_DOWN` handler
					let colIndex = 2; // Z axis default
					if (axisId === 'x') colIndex = 0; // X axis
					if (axisId === 'y') colIndex = 1; // Y axis

					Matrix4.getColumn(state.startTransform, colIndex, worldAxisNormal);

					const centerLocal = new Cartesian3(
						(bounds.current.xMax + bounds.current.xMin) / 2,
						(bounds.current.yMax + bounds.current.yMin) / 2,
						(bounds.current.zMax + bounds.current.zMin) / 2
					);
					const worldCenter = Matrix4.multiplyByPoint(
						state.startTransform,
						centerLocal,
						new Cartesian3()
					);

					const dragPlane = Plane.fromPointNormal(worldCenter, worldAxisNormal);
					const hit = IntersectionTests.rayPlane(ray, dragPlane);

					if (hit) {
						const vStart = Cartesian3.subtract(state.startWorldPos, worldCenter, new Cartesian3());
						const vCurrent = Cartesian3.subtract(hit, worldCenter, new Cartesian3());

						Cartesian3.normalize(vStart, vStart);
						Cartesian3.normalize(vCurrent, vCurrent);

						let dotProduct = Cartesian3.dot(vStart, vCurrent);
						dotProduct = Math.max(-1.0, Math.min(1.0, dotProduct));
						let angle = Math.acos(dotProduct);

						const cross = Cartesian3.cross(vStart, vCurrent, new Cartesian3());
						if (Cartesian3.dot(cross, worldAxisNormal) < 0) {
							angle = -angle;
						}

						let deltaDegrees = CesiumMath.toDegrees(angle);

						// Apply standard mappings to Cesium's internal HeadingPitchRoll
						// Cesium conventions:
						// Heading corresponds to Z-axis (rotation around -Z axis)
						// Pitch corresponds to Y-axis (rotation around -Y axis)
						// Roll corresponds to X-axis (rotation around +X axis)
						rotationRef.current = {
							heading: state.startRotation.heading + (axisId === 'z' ? -deltaDegrees : 0),
							pitch: state.startRotation.pitch + (axisId === 'y' ? -deltaDegrees : 0),
							roll: state.startRotation.roll + (axisId === 'x' ? deltaDegrees : 0),
						};

						updateTransforms();
					}
				}

				// resize logic
				else if (state.type === 'resize') {
					const configId = state.id.replace('resize_', '');
					const config = FACES_CONFIG.find((f) => f.id === configId);
					if (!config) return;

					const worldAxis = new Cartesian3();
					Matrix4.getColumn(
						state.startTransform,
						config.axis === 'x' ? 0 : config.axis === 'y' ? 1 : 2,
						worldAxis
					);

					const camDir = camera.direction;
					let cross1 = Cartesian3.cross(worldAxis, camDir, new Cartesian3());
					if (Cartesian3.magnitude(cross1) < Math.EPSILON5) {
						Cartesian3.cross(worldAxis, camera.up, cross1);
					}
					const dragPlaneNormal = Cartesian3.cross(cross1, worldAxis, new Cartesian3());
					Cartesian3.normalize(dragPlaneNormal, dragPlaneNormal);

					const dragPlane = Plane.fromPointNormal(state.startWorldPos, dragPlaneNormal);
					const hit = IntersectionTests.rayPlane(ray, dragPlane);

					if (hit) {
						const diff = Cartesian3.subtract(hit, state.startWorldPos, new Cartesian3());
						const deltaDistance = Cartesian3.dot(diff, worldAxis);

						const b = bounds.current as any;
						const startB = state.startBounds as any;

						if (config.sign > 0) {
							b[config.id] = startB[config.id] + deltaDistance;
							if (b[config.id] < b[config.opp] + 0.5) b[config.id] = b[config.opp] + 0.5;
						} else {
							b[config.id] = startB[config.id] + deltaDistance;
							if (b[config.id] > b[config.opp] - 0.5) b[config.id] = b[config.opp] - 0.5;
						}

						updatePlanes();
						scene.requestRender();
					}
				}
			};

			// One handler for both: a second registration for the same event replaces the first,
			// which used to switch the hover highlight and the grab cursor off.
			handler.setInputAction((movement: any) => {
				if (dragStateRef.current.type) drag(movement);
				else hover(movement);
			}, ScreenSpaceEventType.MOUSE_MOVE);

			// Drag End
			handler.setInputAction(() => {
				if (dragStateRef.current.type) {
					if (centerRef.current) {
						optionsRef.current.onSaveState({
							center: centerRef.current,
							rotation: { ...rotationRef.current },
							bounds: { ...bounds.current },
						});
					}

					dragStateRef.current = {
						type: null,
						id: null,
						startWorldPos: null,
						startBounds: null,
						startRotation: { heading: 0, pitch: 0, roll: 0 },
					};
					scene.screenSpaceCameraController.enableInputs = true;
					viewer.container.style.cursor = hoveredElementRef.current ? 'grab' : 'default';
					scene.requestRender();
				}
			}, ScreenSpaceEventType.LEFT_UP);
		} catch (err) {
			console.error(err);
			toast.error(t('viewer.clipping.toast.initFailed'));
		}

		return () => {
			if (handlerRef.current) {
				handlerRef.current.destroy();
				handlerRef.current = null;
			}
			if (viewer && !viewer.isDestroyed()) {
				boxEntitiesRef.current.forEach((entity) => viewer.entities.remove(entity));

				viewer.scene.requestRender();
			}
			// Reset all refs explicitly
			boxEntitiesRef.current = [];
			centerEntityRef.current = null;
			resizeEntitiesRef.current = [];
			rotateEntitiesRef.current = [];
		};
	}, [viewer, enabled, hasBox]);
};
